/**
 * VarianceIndicator Component
 * Color-coded variance display with health indicators
 */

import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export interface VarianceIndicatorProps {
  variance: number;
  variancePercent: number;
  showIcon?: boolean;
  showPercentage?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const VarianceIndicator: React.FC<VarianceIndicatorProps> = ({
  variance,
  variancePercent,
  showIcon = true,
  showPercentage = true,
  size = 'md',
}) => {
  // Determine color based on variance (positive = under budget = good)
  const getColorClass = (): string => {
    if (variance > 0) return 'text-green-600';
    if (variancePercent < -5) return 'text-red-600';
    if (variancePercent < 0) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const getBgColorClass = (): string => {
    if (variance > 0) return 'bg-green-50';
    if (variancePercent < -5) return 'bg-red-50';
    if (variancePercent < 0) return 'bg-yellow-50';
    return 'bg-gray-50';
  };

  const getIcon = () => {
    if (variance > 0) return <TrendingUp className={iconSizeClass} />;
    if (variance < 0) return <TrendingDown className={iconSizeClass} />;
    return <Minus className={iconSizeClass} />;
  };

  const textSizeClass = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm';
  const iconSizeClass = size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';
  const paddingClass = size === 'sm' ? 'px-2 py-1' : size === 'lg' ? 'px-3 py-2' : 'px-2.5 py-1.5';

  const formatCurrency = (value: number): string => {
    const absValue = Math.abs(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(absValue);
  };

  return (
    <div
      className={`inline-flex items-center space-x-1.5 rounded-lg ${paddingClass} ${getBgColorClass()} ${getColorClass()} font-medium ${textSizeClass}`}
    >
      {showIcon && getIcon()}
      <span>
        {variance > 0 ? '+' : variance < 0 ? '-' : ''}
        {formatCurrency(variance)}
      </span>
      {showPercentage && (
        <span className="opacity-75">
          ({variance > 0 ? '+' : ''}
          {variancePercent.toFixed(1)}%)
        </span>
      )}
    </div>
  );
};

export default VarianceIndicator;
